import { useContext, useEffect, useRef } from 'react';
import type { StripeCardNumberElement } from '@stripe/stripe-js';
import type braintree from 'braintree-web';
import { NewCardForm } from '../PaymentForms/NewCardForm';
import { CheckoutContext } from '@/context/CheckoutContext';
import { Disclaimer } from '../Common/Disclaimer';
import { PayButton } from '../PaymentInputs/PayButton';
import { calculateTotalPrice } from '@/utils/pricing';
import { SaveCardCheckbox } from '../PaymentInputs/SaveCardCheckbox';
import { useTranslate } from '@/hooks/useTranslate';
import { getMixpanelProperties } from '@/utils/mixpanelProperties';
import { MixpanelEvents } from '@/constants/mixpanelEvents';
import { mixpanelService } from '@/services/mixpanelService';
import { ApprovalText } from '../PaymentInputs/ApprovalText';
import { Button } from '../Common/Button';
import { ButtonType } from '@/types/components';

export const AddNewCardForm = () => {
  const context = useContext(CheckoutContext);
  const {
    card: { setCardElementRef, initialCardPayload, setInitialCardPayload },
    drawer: { closeDrawer },
    payment: { isFormValid },
    pricing,
    actions: { runPayment },
  } = context;
  const { t } = useTranslate();
  const cardElementRef = useRef<StripeCardNumberElement | braintree.HostedFields | null>(null);

  useEffect(() => {
    if (!cardElementRef.current) return;
    setCardElementRef(cardElementRef);
  }, [cardElementRef, setCardElementRef]);

  const handlePayment = () => {
    const properties = getMixpanelProperties(context);
    mixpanelService.track(MixpanelEvents.NEW_CARD_PAYMENT, properties as Record<string, unknown>);
    runPayment({ newCard: true });
  };

  const handleCancel = () => {
    closeDrawer();
  };

  const handleSaveCardChange = (value: boolean) => {
    setInitialCardPayload({ ...initialCardPayload, isSaveCard: value });
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="color-card-foreground font-semibold text-base">{t('payWithDebitOrCreditCard')}</div>
      <Disclaimer />
      <div className="mt-2">
        <NewCardForm />
      </div>
      <div className="flex items-center w-full gap-2 mt-1">
        <SaveCardCheckbox isSaveCard={initialCardPayload?.isSaveCard ?? false} setIsSaveCard={handleSaveCardChange} />
      </div>
      <div className="flex flex-col gap-2 mt-3 w-full text-sm">
        <PayButton price={calculateTotalPrice(pricing)} isDisabled={!isFormValid} onClick={() => handlePayment()} />
      </div>
      <div className="flex flex-col mt-2 w-full">
        <Button type={ButtonType.LIGHT} onClick={() => handleCancel()}>
          {t('cancel')}
        </Button>
        <ApprovalText />
      </div>
    </div>
  );
};
